import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  HiOutlineCheckCircle,
  HiOutlineArrowRight, HiOutlineLockClosed, HiOutlineInformationCircle,
} from "react-icons/hi2";
import PortalLayout from "./PortalLayout";
import { portalAPI } from "./portalApi";
import "./PrescreeningPage.css";

const fmt = (d) => d ? new Date(d).toLocaleDateString("en-US", { year:"numeric", month:"long", day:"numeric" }) : "";

export default function PrescreeningPage() {
  const [loading,   setLoading]   = useState(true);
  const [questions, setQuestions] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [submittedAt, setSubmittedAt] = useState(null);
  const [answers,   setAnswers]   = useState({});
  const [savedAnswers, setSavedAnswers] = useState(null);
  const [error,     setError]     = useState("");
  const [saving,    setSaving]    = useState(false);
  const [done,      setDone]      = useState(false);

  useEffect(() => {
    portalAPI.getPrescreening()
      .then(res => {
        const d = res?.data;
        if (!d) return;
        setQuestions(d.questions || []);
        setSubmitted(d.submitted);
        setSubmittedAt(d.submittedAt);
        setSavedAnswers(d.answers);
      })
      .catch(() => setError("Could not load the questionnaire. Please refresh the page."))
      .finally(() => setLoading(false));
  }, []);

  const sections = [...new Set(questions.map(q => q.section))];

  const handleChange = (id, value) => setAnswers(prev => ({ ...prev, [id]: value }));

  const handleSubmit = async () => {
    setError("");
    const missing = questions.filter(q => q.required && !String(answers[q.id] || "").trim());
    if (missing.length > 0) {
      setError(`Please answer all questions before submitting. ${missing.length} question${missing.length!==1?"s":""} remaining.`);
      return;
    }
    setSaving(true);
    try {
      await portalAPI.submitPrescreening(answers);
      setDone(true);
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const answeredCount = questions.filter(q => String(answers[q.id] || "").trim()).length;
  const progress = questions.length ? Math.round((answeredCount / questions.length) * 100) : 0;

  return (
    <PortalLayout eyebrow="Prescreening" pageTitle="Prescreening Questionnaire"
      quote="Honesty with yourself is the first step of every journey." quoteAuthor="Forge Retreat">

      {loading ? (
        <div className="psc__loading"><div className="psc__spinner"/><p>Loading questionnaire…</p></div>
      ) : error && questions.length === 0 ? (
        <div className="pc psc__error"><p>{error}</p></div>

      ) : (submitted || done) ? (
        /* ── Already submitted — read-only confirmation view ── */
        <div className="psc__done-wrap">
          <div className="pc psc__done-card">
            <div className="psc__done-icon"><HiOutlineCheckCircle/></div>
            <h2 className="psc__done-title">Prescreening Complete</h2>
            <p className="psc__done-sub">
              {done
                ? "Thank you for sharing this with us. Our team will review your answers and follow up with next steps."
                : `You submitted this questionnaire on ${fmt(submittedAt)}.`}
            </p>
            <Link to="/portal/journey" className="p-btn p-btn--fill" style={{marginTop:"1rem",display:"inline-flex"}}>
              View Your Journey <HiOutlineArrowRight/>
            </Link>
          </div>

          {/* Read-only view of their saved answers */}
          {(savedAnswers || done) && (
            <div className="pc psc__answers-card">
              <p className="p-section-title">Your Answers</p>
              {sections.map(section => (
                <div key={section} className="psc__section-block">
                  <p className="psc__section-heading">{section}</p>
                  {questions.filter(q => q.section === section).map(q => (
                    <div key={q.id} className="psc__answer-row">
                      <p className="psc__answer-q">{q.label}</p>
                      <p className="psc__answer-a">{(savedAnswers || answers)[q.id] || "—"}</p>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

      ) : (
        /* ── Active questionnaire ── */
        <div className="psc__form-wrap">
          <div className="pc psc__intro">
            <HiOutlineInformationCircle className="psc__intro-icon"/>
            <div>
              <p className="psc__intro-title">Before you continue</p>
              <p className="psc__intro-sub">
                This questionnaire helps our team understand where you're at and how best to support you.
                There are no right or wrong answers — please be as honest as you can. Your responses are
                confidential and reviewed only by our clinical and support team.
              </p>
            </div>
          </div>

          {/* Progress bar */}
          <div className="psc__progress-wrap">
            <div className="psc__progress-track">
              <div className="psc__progress-fill" style={{ width: `${progress}%` }}/>
            </div>
            <p className="psc__progress-label">{answeredCount} of {questions.length} answered</p>
          </div>

          {sections.map(section => (
            <div key={section} className="pc psc__section-card">
              <p className="p-section-title">{section}</p>
              {questions.filter(q => q.section === section).map(q => (
                <div key={q.id} className="psc__question">
                  <label className="psc__q-label">
                    {q.label} {q.required && <span className="psc__required">*</span>}
                  </label>
                  {q.type === "textarea" ? (
                    <textarea
                      className="psc__textarea"
                      rows={3}
                      value={answers[q.id] || ""}
                      onChange={e => handleChange(q.id, e.target.value)}
                      placeholder="Type your answer here…"
                    />
                  ) : (
                    <div className="psc__radio-group">
                      {q.options.map(opt => (
                        <label key={opt} className={`psc__radio-opt ${answers[q.id]===opt ? "psc__radio-opt--selected" : ""}`}>
                          <input type="radio" name={q.id} value={opt}
                            checked={answers[q.id] === opt}
                            onChange={() => handleChange(q.id, opt)}/>
                          <span>{opt}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}

          {error && <p className="psc__error-msg">{error}</p>}

          <div className="psc__submit-row">
            <p className="psc__submit-note">
              <HiOutlineLockClosed/> Your answers are confidential and cannot be edited once submitted.
            </p>
            <button type="button" className="p-btn p-btn--fill" onClick={handleSubmit} disabled={saving}>
              {saving ? "Submitting…" : "Submit Questionnaire"}
              {!saving && <HiOutlineArrowRight/>}
            </button>
          </div>
        </div>
      )}
    </PortalLayout>
  );
}
